import { AfterViewInit, ChangeDetectionStrategy, Component, DestroyRef, ElementRef, OnDestroy, inject, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EMPTY, Subscription, catchError, filter, from, fromEvent, take } from 'rxjs';
import { createDigitaServiceError } from 'src/app/app-error';
import { MediaRepository } from '../media.repository';
import { MediaService } from '../media.service';
import { MediaNativeVideoModel } from './media-native-video.model';

/**
 * The Media Native Video Provider Component.
 *
 * It is not standalone and belongs within the {@link MediaComponent}.
 *
 * See {@link https://developer.mozilla.org/en-US/docs/Web/HTML/Element/video}
 */
@Component({
  selector: 'app-media-native-video',
  templateUrl: './media-native-video.component.html',
  styleUrls: ['./media-native-video.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MediaNativeVideoComponent implements AfterViewInit, OnDestroy {
  protected readonly mediaRepository = inject(MediaRepository);
  private readonly mediaService = inject(MediaService);
  private readonly destroyRef = inject(DestroyRef);

  /**
   * A reference to the media HTMLVideoElement.
   */
  private readonly mediaElement = viewChild.required<ElementRef<HTMLVideoElement>>('media');

  /**
   * A reference to the html video element from the media element ref.
   */
  private player?: HTMLVideoElement;

  /**
   * Holds the in-flight play() promise subscription so a pause can cancel it.
   */
  private playSubscription?: Subscription;

  /////////////////////////////////////////////////////////////////////
  // LIFECYCLE
  /////////////////////////////////////////////////////////////////////

  /**
   * Lifecycle
   */
  ngAfterViewInit() {
    // a reference to the player
    this.player = this.mediaElement().nativeElement;

    // this component only renders once the media is initialized, so the settings are guaranteed to exist
    const options = this.mediaRepository.videoSettings();
    if (!options) {
      throw createDigitaServiceError('MediaNativeVideoComponent', 'ngAfterViewInit', 'No video settings available.', 'internal');
    }
    this.createPlayer(options);
  }

  /**
   * Lifecycle
   */
  ngOnDestroy() {
    this.playSubscription?.unsubscribe();
  }

  /////////////////////////////////////////////////////////////////////
  // PLAYER CREATION
  /////////////////////////////////////////////////////////////////////

  /**
   * Create the player.
   *
   * @param options - the video options
   */
  private createPlayer(options: MediaNativeVideoModel) {
    const player = this.player;

    // if there is no player element then that is a unexpected error
    if (!player) {
      throw createDigitaServiceError('MediaNativeVideoComponent', 'createPlayer', 'No player found', 'internal');
    }

    // set the top level attributes
    player.controls = options.controls;
    player.loop = options.loop;
    player.playsInline = options.playsinline;
    player.muted = options.muted;
    player.autoplay = options.autoplay;
    if (options.pip === false) {
      player.disablePictureInPicture = true;
    }
    if (options.download === false) {
      // controlsList is absent from the TypeScript DOM lib, so it is set as an attribute
      player.setAttribute('controlsList', 'nodownload');
    }
    if (options.poster) {
      player.poster = options.poster;
    }

    // the preload method determines when "ready" is called.
    if (options.preload) {
      // if we are preloading the entire source, then we will wait for the video to fully download
      // before declaring itself ready
      // See https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/canplaythrough_event
      fromEvent(player, 'canplaythrough')
        .pipe(
          filter(() => player.readyState === 4),
          take(1),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe(() => {
          // once ready we will find the total time of the video
          this.updateDuration();

          // inform the store that the video is ready
          this.mediaService.applyReady();
        });
    } else {
      // if we are not preloading the entire source, then we will try to play as soon as possible
      // before declaring itself ready
      // see https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/canplay_event
      fromEvent(player, 'canplay')
        .pipe(
          filter(() => player.readyState === 3 || player.readyState === 4),
          take(1),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe(() => {
          // once ready we will find the total time of the video
          this.updateDuration();

          // inform the store that the video is ready
          this.mediaService.applyReady();
        });
    }

    // listen out for when the media has ended
    fromEvent(player, 'ended')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.videoEnded();
      });

    // listen out for when the media has started playing
    fromEvent(player, 'play')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.videoPlaying();
      });

    // listen out for when the media has paused
    fromEvent(player, 'pause')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.videoPaused();
      });

    // listen out for time updates
    fromEvent(player, 'timeupdate')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.updateCurrentTime();
      });

    // listen out for duration change events
    fromEvent(player, 'durationchange')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.updateDuration();
      });

    // listen out for errors
    fromEvent(player, 'error')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.onError();
      });

    // create integrations
    this.createPlayerIntegration();

    // figure out what source can be played
    let source: string | undefined = undefined;
    for (const src of options.sources) {
      if (player.canPlayType(src.type)) {
        source = src.src;
        break;
      }
    }

    // finally play the source
    if (source) {
      player.src = source;
      player.load();
    } else {
      this.onError('No compatible source found for this browser.');
    }
  }

  /**
   * Integrates the player with the MediaService of the application.
   */
  private createPlayerIntegration() {
    // listen for play events.
    this.mediaService.play$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.play();
    });

    // listen for pause events.
    this.mediaService.pause$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.pause();
    });

    // seek to the current time
    this.mediaService.seekTo$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
      if (this.player && data) {
        this.player.currentTime = data.seconds;
        if (data.play) {
          this.play();
        } else {
          this.pause();
        }
      }
    });

    // mute the media
    this.mediaService.mute$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.player) {
        this.player.muted = true;
      }
    });

    // unMute the media
    this.mediaService.unMute$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.player) {
        this.player.muted = false;
      }
    });

    // set the volume
    this.mediaService.volume$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((volume) => {
      if (typeof volume === 'number' && volume >= 0 && volume <= 100) {
        if (this.player) {
          this.player.volume = volume / 100;
        }
      }
    });
  }

  /**
   * The Video has ended.
   */
  private videoEnded() {
    if (this.player) {
      this.mediaService.applyCurrentTime(this.player.duration);
    }
    this.mediaService.applyEnded();
  }

  /**
   * Occurs when the video is playing.
   */
  private videoPlaying() {
    this.mediaService.applyPlaying();
  }

  /**
   * Occurs when the video is paused.
   */
  private videoPaused() {
    this.mediaService.applyPaused();
  }

  /////////////////////////////////////////////////////////////////////
  // UTILS
  /////////////////////////////////////////////////////////////////////

  /**
   * Retrieve the current time from the player and save it to the store.
   */
  private updateCurrentTime() {
    const currentTime = this.player?.currentTime || 0;
    this.mediaService.applyCurrentTime(Math.floor(currentTime));
  }

  /**
   * Retrieve the total duration from the player and save it to the store.
   */
  private updateDuration() {
    const duration = this.player?.duration || 0;
    this.mediaService.applyDuration(Math.floor(duration));
  }

  /**
   * Occurs when an error occurs.
   *
   * @param reason - The reason for the error.
   */
  private onError(reason?: string) {
    this.mediaService.applyError(reason);
  }

  /////////////////////////////////////////////////////////////////////
  // CONTROLS
  /////////////////////////////////////////////////////////////////////

  /**
   * Play the video.
   */
  play() {
    // cancel any in progress play attempt
    this.playSubscription?.unsubscribe();

    // if the player doesn't exist then abort
    if (!this.player) {
      return;
    }

    // play the video; a rejected play() (e.g. autoplay policy) fails silently
    this.playSubscription = from(this.player.play())
      .pipe(
        take(1),
        catchError((event) => {
          console.error(event);
          return EMPTY;
        }),
      )
      .subscribe();
  }

  /**
   * Pause the video.
   */
  pause() {
    // cancel any in progress play attempt
    this.playSubscription?.unsubscribe();

    // if the player exists then pause it
    this.player?.pause();
  }

  /////////////////////////////////////////////////////////////////////
  // TEMPLATE EVENTS
  /////////////////////////////////////////////////////////////////////

  /**
   * Occurs on pointer up from the touch div when the video has no controls.
   */
  controlZonePointerUp() {
    if (this.player) {
      if (this.player.paused) {
        this.play();
      } else {
        this.pause();
      }
    }
  }
}
